import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MotiView } from 'moti';
import { memo, useMemo } from 'react';
import { DimensionValue, StyleSheet, View } from 'react-native';

/**
 * A case card while it loads, laid out as `CaseCard` so the real card
 * takes its place without a shift. `withFooter` stands in for its tags or
 * suggestion reason.
 */
export const CaseCardSkeleton = memo(
  ({ withFooter = true }: { withFooter?: boolean }) => {
    const styles = useStyles();
    const bar = (width: DimensionValue, height: number, marginTop = 0) => (
      <View style={[styles.bar, { width, height, marginTop }]} />
    );
    return (
      <MotiView
        from={{ opacity: 1 }}
        animate={{ opacity: 0.55 }}
        transition={{ type: 'timing', duration: 800, loop: true }}
        style={styles.card}
      >
        <View style={styles.top}>
          {bar('28%', styles.chip.height)}
          {bar('18%', styles.levels.height)}
        </View>
        {bar('72%', styles.title.height, styles.title.marginTop)}
        {bar('100%', styles.line.height, styles.line.marginTop)}
        {bar('86%', styles.line.height, styles.lineNext.marginTop)}
        {bar('36%', styles.meta.height, styles.meta.marginTop)}
        {withFooter ? (
          <View style={styles.footer}>
            {bar('16%', styles.tag.height)}
            {bar('12%', styles.tag.height)}
            {bar('14%', styles.tag.height)}
          </View>
        ) : null}
      </MotiView>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        card: {
          flex: 1,
          borderRadius: rpx(14),
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          padding: rpx(16),
        },
        bar: { borderRadius: rpx(6), backgroundColor: aiColors.skeleton },
        top: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        chip: { height: rpx(18) },
        levels: { height: rpx(6) },
        title: { height: rpx(16), marginTop: rpx(12) },
        line: { height: rpx(12), marginTop: rpx(8) },
        lineNext: { marginTop: rpx(6) },
        meta: { height: rpx(11), marginTop: rpx(14) },
        tag: { height: rpx(18) },
        footer: { flexDirection: 'row', gap: rpx(6), marginTop: rpx(12) },
      }),
    [rpx],
  );
}
